import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { VehicleFields } from "@/pages/ClientDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Plus, Search, Loader2, User } from "lucide-react";
import { toast } from "sonner";

export default function Vehicles() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", make: "", model: "", version: "", year: "", plate: "", color: "", mileage: "", vehicle_type: "", notes: "", photos: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/vehicles", { params: q ? { search: q } : {} });
      setVehicles(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); }, [load]);
  useEffect(() => { if (params.get("new")) { setOpen(true); setParams({}); } }, [params, setParams]);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search, load]);

  const save = async () => {
    if (!form.client_id) return toast.error("Sélectionnez un client");
    if (!form.make) return toast.error("La marque est requise");
    setSaving(true);
    try {
      await api.post("/vehicles", form);
      toast.success("Véhicule créé");
      setOpen(false);
      setForm({ client_id: "", make: "", model: "", version: "", year: "", plate: "", color: "", mileage: "", vehicle_type: "", notes: "", photos: [] });
      load(search);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Véhicules"
        subtitle={`${vehicles.length} véhicule${vehicles.length > 1 ? "s" : ""}`}
        icon={Car}
        actions={
          <Button onClick={() => setOpen(true)} data-testid="btn-new-vehicle" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau véhicule
          </Button>
        }
      />

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (marque, modèle, immatriculation)" data-testid="input-search-vehicles" className="pl-9 bg-white border-slate-200" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : vehicles.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucun véhicule.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => (
            <button key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} data-testid={`vehicle-card-${v.id}`} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-500/40 transition-all">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-4 h-4 text-emerald-600" />
                <span className="font-medium text-slate-900">{v.make} {v.model}</span>
              </div>
              <div className="text-xs text-slate-500 mb-2">{[v.year, v.plate, v.color].filter(Boolean).join(" · ")}</div>
              {v.client_name && <div className="text-xs text-slate-500 flex items-center gap-1.5"><User className="w-3 h-3" />{v.client_name}</div>}
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">Nouveau véhicule</DialogTitle></DialogHeader>
          <div className="space-y-1.5 mb-3">
            <Label className="text-xs text-slate-500">Client *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="select-vehicle-client" className="bg-slate-50 border-slate-200"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-700">
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <VehicleFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-vehicle" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
